import React, { useMemo } from 'react'
import { Button, Icon, Tag } from 'antd'

import {
  formatCount,
  getGeneratedAssets,
  getNextPhaseLabel,
  getPhaseCount,
  getReferenceAssets,
  getStoryboardPlanCount,
  getPhaseView,
  isEntityVerified,
  isStoryboardPlanApproved
} from '../../utils'
import AssetPreview from '../AssetPreview'
import EntityListPreview from '../EntityListPreview'
import PhaseSummaryCard from '../PhaseSummaryCard'
import ReferenceRequestCard from '../ReferenceRequestCard'
import StoryboardPlanPreview from '../StoryboardPlanPreview'
import './PhaseWorkspace.scss'

const getEntityPrimaryAction = ({
  currentPhase,
  phaseData,
  phaseSteps,
  onExtractPhase,
  onVerifyAllPhase,
  onGeneratePhase,
  onAdvancePhase
}) => {
  const view = getPhaseView(currentPhase)
  const currentPhaseData = phaseData[currentPhase] || {}
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const hasExtracted = Boolean(
    currentPhaseData.extracted || currentPhaseData.count
  )
  const hasGenerated = Boolean(assets.length)
  const entities = currentPhaseData.entities || []
  const allEntitiesVerified =
    !entities.length || entities.every(isEntityVerified)
  const hasCastDynamicsReady =
    currentPhase === 'characters' && currentPhaseData.castDynamicsUpdated

  if (!hasExtracted) {
    return {
      label: `Extract ${view.primaryNoun}`,
      icon: 'scan',
      loadingKey: `extract:${currentPhase}`,
      onClick: onExtractPhase
    }
  }

  if (!allEntitiesVerified) {
    return {
      label: `Verify all ${view.primaryNoun}`,
      icon: 'check-circle',
      loadingKey: `verify-all:${currentPhase}`,
      disabled: !onVerifyAllPhase,
      onClick: () => onVerifyAllPhase(currentPhase)
    }
  }

  if (!hasGenerated) {
    return {
      label: hasCastDynamicsReady
        ? 'Confirm & generate character assets'
        : `Generate ${view.generatedLabel}`,
      icon: 'picture',
      loadingKey: `generate:${currentPhase}`,
      onClick: onGeneratePhase
    }
  }

  return {
    label: `Continue to ${getNextPhaseLabel(phaseSteps, currentPhase)}`,
    icon: 'arrow-right',
    loadingKey: `advance:${currentPhase}`,
    onClick: onAdvancePhase
  }
}

const getStoryboardPrimaryAction = ({
  phaseData,
  phaseSteps,
  onGeneratePhase,
  onGenerateStoryboardPlan,
  onAdvancePhase
}) => {
  const storyboardData = phaseData.storyboard || {}
  const assets = getGeneratedAssets(phaseData, 'storyboard')
  const hasPlan = getStoryboardPlanCount(storyboardData.plan) > 0
  const hasGenerated = Boolean(assets.length)

  if (!hasPlan) {
    return {
      label: 'Extract plan',
      icon: 'profile',
      loadingKey: 'storyboard:plan',
      onClick: onGenerateStoryboardPlan
    }
  }

  if (!hasGenerated) {
    return {
      label: 'Generate storyboard',
      icon: 'picture',
      loadingKey: 'generate:storyboard',
      onClick: () => onGeneratePhase('storyboard')
    }
  }

  return {
    label: `Continue to ${getNextPhaseLabel(phaseSteps, 'storyboard')}`,
    icon: 'arrow-right',
    loadingKey: 'advance:storyboard',
    onClick: onAdvancePhase
  }
}

const getFinalPrimaryAction = ({ isFinalized, onFinalizeCreativeDirection }) =>
  isFinalized
    ? null
    : {
        label: 'Generate Finalized Report',
        icon: 'book',
        loadingKey: 'finalize',
        onClick: onFinalizeCreativeDirection
      }

const getPhasePrimaryAction = ({
  currentPhase,
  phaseData,
  phaseSteps,
  isFinalized,
  onExtractPhase,
  onVerifyAllPhase,
  onGeneratePhase,
  onGenerateStoryboardPlan,
  onAdvancePhase,
  onFinalizeCreativeDirection
}) => {
  const isEntityPhase = ['characters', 'outfits', 'locations'].includes(
    currentPhase
  )

  if (isEntityPhase) {
    return getEntityPrimaryAction({
      currentPhase,
      phaseData,
      phaseSteps,
      onExtractPhase,
      onVerifyAllPhase,
      onGeneratePhase,
      onAdvancePhase
    })
  }

  if (currentPhase === 'storyboard') {
    return getStoryboardPrimaryAction({
      phaseData,
      phaseSteps,
      onGeneratePhase,
      onGenerateStoryboardPlan,
      onAdvancePhase
    })
  }

  if (currentPhase === 'finalized') {
    return getFinalPrimaryAction({
      isFinalized,
      onFinalizeCreativeDirection
    })
  }

  return null
}

const getPhaseSecondaryActions = ({
  currentPhase,
  phaseData,
  onExtractPhase,
  onGeneratePhase,
  onGenerateStoryboardPlan
}) => {
  const currentPhaseData = phaseData[currentPhase] || {}
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const hasGeneratedAssets = assets.length > 0
  const hasExtracted = Boolean(
    currentPhaseData.extracted || currentPhaseData.count
  )
  const hasStoryboardPlan =
    currentPhase === 'storyboard' &&
    getStoryboardPlanCount(currentPhaseData.plan) > 0

  if (
    ['characters', 'outfits', 'locations'].includes(currentPhase) &&
    hasExtracted &&
    !hasGeneratedAssets
  ) {
    return [
      {
        label: 'Extract again',
        icon: 'reload',
        loadingKey: `extract:${currentPhase}`,
        onClick: onExtractPhase
      }
    ]
  }

  if (currentPhase === 'storyboard') {
    if (hasStoryboardPlan && !hasGeneratedAssets) {
      return [
        {
          label: 'Regenerate plan',
          icon: 'reload',
          loadingKey: 'storyboard:plan',
          onClick: onGenerateStoryboardPlan
        }
      ]
    }

    if (hasGeneratedAssets) {
      return [
        {
          label: 'Regenerate storyboard',
          icon: 'reload',
          loadingKey: 'generate:storyboard',
          onClick: () => onGeneratePhase('storyboard')
        }
      ]
    }
  }

  return []
}

const PhaseWorkspace = ({
  currentPhase,
  phaseSteps,
  phaseData,
  creativeGuideline,
  isFinalized,
  creativeStatement,
  hasConversation,
  isStreaming,
  isSynthesizingVision,
  isLockingVision,
  isVisionLocked,
  isActivePhase,
  isSelectedPhase,
  showActions = true,
  phaseBusyAction,
  acceptedExtensions,
  referenceUploadProgress,
  referenceUploadStatus,
  referenceUploadMessage,
  onExtractPhase,
  onVerifyEntity,
  onVerifyAllPhase,
  onGeneratePhase,
  onGenerateStoryboardPlan,
  onRefreshGenerationStatus,
  onUploadRequestedReference,
  onAdvancePhase,
  onFinalizeCreativeDirection,
  onSynthesizeVision,
  onLockVision,
  onViewFinalizedReport,
  onTogglePhase,
  isDrawerView = false
}) => {
  const view = getPhaseView(currentPhase)
  const isBusy = Boolean(phaseBusyAction)
  const currentPhaseData = phaseData[currentPhase] || {}
  const count = getPhaseCount(phaseData, currentPhase)
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const referenceUploads = getReferenceAssets(phaseData, currentPhase)
  const hasGeneratedAssets = assets.length > 0
  const isEntityPhase = ['characters', 'outfits', 'locations'].includes(
    currentPhase
  )
  const isVisionPhase = currentPhase === 'vision'
  const isStoryboardPhase = currentPhase === 'storyboard'
  const isFinalPhase = currentPhase === 'finalized'
  const hasFinalizedReport = isFinalPhase && isFinalized && creativeGuideline
  const hasVisionStatement = Boolean(String(creativeStatement || '').trim())
  const shouldShowFullPhase = isSelectedPhase
  const storyboardNeedsChat =
    isStoryboardPhase && isActivePhase && !hasGeneratedAssets
  const hasStoryboardPlan =
    isStoryboardPhase && getStoryboardPlanCount(currentPhaseData.plan) > 0

  const primaryAction = useMemo(() => {
    if (!isActivePhase) return null

    return getPhasePrimaryAction({
      currentPhase,
      phaseData,
      phaseSteps,
      isFinalized,
      onExtractPhase,
      onVerifyAllPhase,
      onGeneratePhase,
      onGenerateStoryboardPlan,
      onAdvancePhase,
      onFinalizeCreativeDirection
    })
  }, [
    currentPhase,
    isFinalized,
    isActivePhase,
    onAdvancePhase,
    onExtractPhase,
    onFinalizeCreativeDirection,
    onGeneratePhase,
    onGenerateStoryboardPlan,
    onVerifyAllPhase,
    phaseData,
    phaseSteps
  ])
  const secondaryActions = useMemo(
    () =>
      isActivePhase
        ? getPhaseSecondaryActions({
            currentPhase,
            phaseData,
            onExtractPhase,
            onGeneratePhase,
            onGenerateStoryboardPlan
          })
        : [],
    [
      currentPhase,
      isActivePhase,
      onExtractPhase,
      onGeneratePhase,
      onGenerateStoryboardPlan,
      phaseData
    ]
  )

  const renderPhaseActions = ({ compact = false } = {}) => {
    if (
      !showActions ||
      (!primaryAction && !secondaryActions.length && !hasFinalizedReport)
    ) {
      return null
    }

    const actionRowClass = compact
      ? 'creative-direction-chat__phase-summary-actions'
      : 'creative-direction-chat__phase-action-row'

    return (
      <div className={actionRowClass}>
        {hasFinalizedReport ? (
          <Button icon="profile" onClick={onViewFinalizedReport}>
            View Finalized Report
          </Button>
        ) : null}
        {secondaryActions.map(action => (
          <Button
            key={action.loadingKey || action.label}
            className="creative-direction-chat__phase-action-button creative-direction-chat__phase-action-button--secondary"
            icon={action.icon}
            loading={phaseBusyAction === action.loadingKey}
            disabled={isBusy && phaseBusyAction !== action.loadingKey}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
        {primaryAction ? (
          <Button
            type="primary"
            className="creative-direction-chat__phase-action-button creative-direction-chat__phase-action-button--primary"
            icon={primaryAction.icon}
            loading={
              (primaryAction.loadingKey &&
                phaseBusyAction === primaryAction.loadingKey) ||
              String(phaseBusyAction || '').endsWith(`:${currentPhase}`)
            }
            disabled={
              primaryAction.disabled ||
              (isBusy && phaseBusyAction !== primaryAction.loadingKey)
            }
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
        ) : null}
      </div>
    )
  }

  const taskTitle = isFinalPhase
    ? isFinalized
      ? 'Creative guideline is ready'
      : 'Ready to build the creative guideline'
    : isVisionPhase
    ? isVisionLocked
      ? 'Vision statement locked'
      : hasVisionStatement
      ? 'Vision statement ready'
      : 'Prepare visual vision'
    : isStoryboardPhase && !hasStoryboardPlan
    ? 'Plan storyboard in chat'
    : isStoryboardPhase && hasStoryboardPlan
    ? 'Review storyboard plan'
    : count
    ? `AI found ${formatCount(count)} ${view.primaryNoun}`
    : `Prepare ${view.primaryNoun}`

  const taskDescription = isFinalPhase
    ? 'Finalize everything into the document used by the shot list step.'
    : isVisionPhase
    ? 'Review the visual direction before moving into characters and production references.'
    : !isActivePhase
    ? 'You are reviewing this completed step. The current task remains below in the conversation.'
    : isStoryboardPhase
    ? 'Generate the plan from the conversation, then generate storyboard sheets when the plan is approved.'
    : 'Run each step in order. Sharelook will stop you only when something still needs approval.'

  if (!isActivePhase && !shouldShowFullPhase) {
    return (
      <PhaseSummaryCard
        phase={currentPhase}
        phaseData={phaseData}
        isFinalized={isFinalized}
        onTogglePhase={onTogglePhase}
      />
    )
  }

  if (isActivePhase && !shouldShowFullPhase) {
    return (
      <section className="creative-direction-chat__phase-summary creative-direction-chat__phase-summary--active">
        <div className="creative-direction-chat__phase-summary-icon">
          <Icon type={view.icon} />
        </div>
        <div className="creative-direction-chat__phase-summary-content">
          <span>{view.label}</span>
          <strong>{taskTitle}</strong>
          <small>{taskDescription}</small>
        </div>
        <div className="creative-direction-chat__phase-summary-controls">
          {!isFinalPhase ? (
            <Button
              size="small"
              className="creative-direction-chat__phase-summary-toggle"
              onClick={onTogglePhase}
            >
              View details
            </Button>
          ) : null}
          {renderPhaseActions({ compact: true })}
        </div>
      </section>
    )
  }

  if (isDrawerView && isVisionPhase) {
    return (
      <section className="creative-direction-chat__phase-drawer-panel creative-direction-chat__phase-workspace creative-direction-chat__vision-drawer-panel">
        <div className="creative-direction-chat__phase-drawer-overview creative-direction-chat__phase-drawer-overview--vision">
          <div>
            <span>Vision status</span>
            <strong>{isVisionLocked ? 'Locked' : 'In progress'}</strong>
          </div>
          <div>
            <span>Statement</span>
            <strong>{hasVisionStatement ? 'Ready' : 'Not created'}</strong>
          </div>
          <div>
            <span>Conversation</span>
            <strong>{hasConversation ? 'Started' : 'Waiting'}</strong>
          </div>
        </div>

        <section className="creative-direction-chat__vision-drawer-content">
          <div className="creative-direction-chat__subheading">
            <span>Vision statement</span>
            {isVisionLocked ? <Tag color="green">Saved</Tag> : null}
          </div>

          {hasVisionStatement ? (
            <div className="creative-direction-chat__vision-drawer-text">
              {creativeStatement}
            </div>
          ) : (
            <div className="creative-direction-chat__vision-empty creative-direction-chat__vision-empty--drawer">
              <Icon type="info-circle" />
              <span>
                When the conversation feels ready, create the vision statement.
              </span>
            </div>
          )}
        </section>

        {isActivePhase ? (
          <div className="creative-direction-chat__phase-action-row">
            <Button
              icon={hasVisionStatement ? 'reload' : 'star'}
              loading={isSynthesizingVision}
              disabled={
                isStreaming ||
                isVisionLocked ||
                isLockingVision ||
                !onSynthesizeVision
              }
              onClick={() => onSynthesizeVision({ showError: true })}
            >
              {hasVisionStatement
                ? 'Update vision statement'
                : 'Create vision statement'}
            </Button>
            <Button
              type="primary"
              icon="check-circle"
              loading={isLockingVision}
              disabled={
                isStreaming ||
                isVisionLocked ||
                !hasVisionStatement ||
                !onLockVision
              }
              onClick={onLockVision}
            >
              Lock vision
            </Button>
          </div>
        ) : null}
      </section>
    )
  }

  if (isDrawerView && isStoryboardPhase) {
    return (
      <section className="creative-direction-chat__phase-drawer-panel creative-direction-chat__phase-workspace">
        {hasGeneratedAssets ? (
          <section className="creative-direction-chat__drawer-section">
            <div className="creative-direction-chat__subheading">
              <span>Generated visuals</span>
              <Button
                size="small"
                icon="sync"
                disabled={isBusy || !isActivePhase}
                loading={phaseBusyAction === 'generation-status'}
                onClick={onRefreshGenerationStatus}
              >
                Refresh
              </Button>
            </div>
            <AssetPreview
              assets={assets}
              previewAssets={assets}
              entities={currentPhaseData.entities || []}
              emptyLabel="Storyboard panel"
              variant="drawer"
              latestAssetIds={currentPhaseData.activeSheetIds}
            />
          </section>
        ) : null}
        {hasStoryboardPlan ? (
          <StoryboardPlanPreview
            plan={currentPhaseData.plan}
            committedPanels={currentPhaseData.committedPanels}
            planApproved={isStoryboardPlanApproved(phaseData)}
            showAll
          />
        ) : (
          <div className="creative-direction-chat__quiet-note">
            <Icon type="info-circle" />
            <span>No storyboard plan has been extracted yet.</span>
          </div>
        )}
        {renderPhaseActions()}
      </section>
    )
  }

  if (isDrawerView && isEntityPhase) {
    const entities = currentPhaseData.entities || []
    const verifiedCount = entities.filter(isEntityVerified).length

    return (
      <section className="creative-direction-chat__phase-drawer-panel creative-direction-chat__phase-workspace">
        <div className="creative-direction-chat__phase-drawer-overview">
          <div>
            <span>{view.label}</span>
            <strong>
              {formatCount(entities.length || count)} {view.primaryNoun}
            </strong>
          </div>
          <div>
            <span>Verified</span>
            <strong>{formatCount(verifiedCount)}</strong>
          </div>
          <div>
            <span>Generated visuals</span>
            <strong>{formatCount(assets.length)}</strong>
          </div>
          {referenceUploads.length ? (
            <div>
              <span>References</span>
              <strong>{formatCount(referenceUploads.length)}</strong>
            </div>
          ) : null}
        </div>
        <div className="creative-direction-chat__phase-drawer-scroll">
          {hasGeneratedAssets ? (
            <section className="creative-direction-chat__drawer-section">
              <div className="creative-direction-chat__subheading">
                <span>Generated visuals</span>
                <Button
                  size="small"
                  icon="sync"
                  disabled={isBusy || !isActivePhase}
                  loading={phaseBusyAction === 'generation-status'}
                  onClick={onRefreshGenerationStatus}
                >
                  Refresh
                </Button>
              </div>
              <AssetPreview
                assets={assets}
                previewAssets={[...referenceUploads, ...assets]}
                entities={currentPhaseData.entities || []}
                emptyLabel={view.generatedLabel || 'Pending'}
                variant="drawer"
              />
            </section>
          ) : null}

          {referenceUploads.length ? (
            <section className="creative-direction-chat__drawer-section">
              <div className="creative-direction-chat__subheading">
                <span>
                  {`Uploaded reference${
                    referenceUploads.length === 1 ? '' : 's'
                  }`}
                </span>
              </div>
              <AssetPreview
                assets={referenceUploads}
                previewAssets={[...referenceUploads, ...assets]}
                entities={currentPhaseData.entities || []}
                emptyLabel="Uploaded reference"
                variant="drawer"
              />
            </section>
          ) : null}

          <EntityListPreview
            entities={entities}
            phase={currentPhase}
            isActivePhase={isActivePhase}
            phaseBusyAction={phaseBusyAction}
            onVerifyEntity={onVerifyEntity}
            showFullDescriptions
          />
        </div>
        {renderPhaseActions()}
      </section>
    )
  }

  return (
    <section className="creative-direction-chat__decision-panel creative-direction-chat__phase-workspace">
      <div className="creative-direction-chat__phase-hero">
        <div className="creative-direction-chat__phase-icon">
          <Icon type={view.icon} />
        </div>
        <div>
          <h3>{taskTitle}</h3>
          <p>{taskDescription}</p>
        </div>
        {isSelectedPhase ? (
          <Button size="small" onClick={onTogglePhase}>
            Hide details
          </Button>
        ) : storyboardNeedsChat ? (
          <Tag color="blue">Use chat</Tag>
        ) : !primaryAction ? (
          <Tag color="green">Complete</Tag>
        ) : null}
      </div>

      {storyboardNeedsChat ? (
        <div className="creative-direction-chat__quiet-note">
          <Icon type="info-circle" />
          <span>
            Use the buttons when you are ready, or keep chatting to refine the
            storyboard before generating anything.
          </span>
        </div>
      ) : null}

      <ReferenceRequestCard
        phase={currentPhase}
        referenceRequest={currentPhaseData.referenceRequest}
        acceptedExtensions={acceptedExtensions}
        phaseBusyAction={phaseBusyAction}
        uploadProgress={referenceUploadProgress}
        uploadStatus={referenceUploadStatus}
        uploadMessage={referenceUploadMessage}
        onUploadReference={onUploadRequestedReference}
      />

      {currentPhase === 'characters' &&
      currentPhaseData.castDynamicsUpdated &&
      !hasGeneratedAssets ? (
        <div className="creative-direction-chat__quiet-note creative-direction-chat__quiet-note--success">
          <Icon type="check-circle" />
          <span>
            Cast dynamics are ready. Confirm them to start character image
            generation.
          </span>
        </div>
      ) : null}

      {isStoryboardPhase && hasStoryboardPlan ? (
        <StoryboardPlanPreview
          plan={currentPhaseData.plan}
          committedPanels={currentPhaseData.committedPanels}
          planApproved={isStoryboardPlanApproved(phaseData)}
        />
      ) : null}

      <EntityListPreview
        entities={currentPhaseData.entities || []}
        phase={currentPhase}
        isActivePhase={isActivePhase}
        phaseBusyAction={phaseBusyAction}
        onVerifyEntity={onVerifyEntity}
      />

      {referenceUploads.length ? (
        <>
          <div className="creative-direction-chat__subheading">
            <span>
              {`Uploaded reference${referenceUploads.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <AssetPreview
            assets={referenceUploads}
            entities={currentPhaseData.entities || []}
            emptyLabel="Uploaded reference"
          />
        </>
      ) : null}

      {!isFinalPhase && hasGeneratedAssets ? (
        <>
          <div className="creative-direction-chat__subheading">
            <span>Generated visuals</span>
            <Button
              size="small"
              icon="sync"
              disabled={isBusy || !isActivePhase}
              loading={phaseBusyAction === 'generation-status'}
              onClick={onRefreshGenerationStatus}
            >
              Refresh
            </Button>
          </div>
          <AssetPreview
            assets={assets}
            entities={currentPhaseData.entities || []}
            emptyLabel={view.generatedLabel || 'Pending'}
          />
        </>
      ) : null}

      {isEntityPhase && currentPhaseData.count ? (
        <div className="creative-direction-chat__quiet-note">
          <Icon type="info-circle" />
          <span>
            Verify all items, or review them one by one before generating
            visuals.
          </span>
        </div>
      ) : null}

      {renderPhaseActions()}
    </section>
  )
}

export default PhaseWorkspace
